import React, { ReactElement, useEffect } from 'react';
import styles from './Step.scss';
import Tooltip from '../Tooltip';
import trackApi from '../../utils/trackApi';
import DoneIcon from '@mui/icons-material/Done';
import Button from '../MaterialUi/Button';

export interface IStepProps {
    _isActive?: boolean;
    _isDone?: boolean;
    _isLast?: boolean;
    _number?: number;
    _nextHandler?: (number: number) => void;
    _goBackHandler?: (number: number) => void;
    title: string;
    tooltip?: string | ReactElement;
    nextHandler?: (data: any) => any | Promise<any>;
    withoutForm?: boolean;
    buttonText?: string;
    buttonDisclaimer?: string | ReactElement;
    disabled?: boolean;
    isSubmitting?: boolean;
    trackPageviewUrl?: string;
}

const Step: React.FC<React.PropsWithChildren<IStepProps>> = ({
    nextHandler,
    title,
    tooltip = null,
    buttonText,
    buttonDisclaimer,
    withoutForm = false,
    disabled = false,
    isSubmitting = false,
    trackPageviewUrl = '',
    children,
    _number,
    _isActive,
    _isDone,
    _isLast,
    _nextHandler,
    _goBackHandler,
}) => {
    const cnButton = [styles.head, _isActive ? styles.active : null, _isDone ? styles.done : null].join(' ');

    const [isLoading, setIsLoading] = React.useState(_isDone);

    const timerIdRef = React.useRef(null);

    const showAsActive = _isActive || (_isLast && _isDone);

    useEffect(() => {
        return () => {
            clearTimeout(timerIdRef.current);
        };
    }, []);

    useEffect(() => {
        setIsLoading(_isDone);
    }, [_isDone]);

    useEffect(() => {
        if (trackPageviewUrl && showAsActive) {
            trackApi.pageview(trackPageviewUrl);
        }
    }, [trackPageviewUrl, showAsActive]);

    const onSubmit = (ev: React.FormEvent) => {
        ev.preventDefault();
        setIsLoading(true);

        if (nextHandler) {
            const nextHandlerRes = nextHandler(ev.target);
            if (nextHandlerRes && typeof nextHandlerRes.then === 'function') {
                nextHandlerRes
                    .then(() => {
                        setTimeout(() => {
                            setIsLoading(false);
                        }, 100);
                    })
                    .catch(() => {
                        setTimeout(() => {
                            setIsLoading(false);
                        }, 100);
                    });
            } else {
                clearTimeout(timerIdRef.current);
                timerIdRef.current = setTimeout(() => {
                    setIsLoading(false);
                }, 1000);
            }
        }
        _nextHandler(_number);
    };

    const onGoBack = () => {
        _goBackHandler(_number);
    };

    return (
        <div className={styles.step}>
            <button tabIndex={_number} className={cnButton} onClick={onGoBack} disabled={disabled}>
                <span className={styles.index}>{_isDone ? <DoneIcon sx={{ fontSize: '1rem' }} /> : _number}</span>
                <span>{title}</span>
                {tooltip && _isActive && <Tooltip className={styles.tooltip}>{tooltip}</Tooltip>}
            </button>
            <section>
                {_isLast ? <div className={styles.noConnector} /> : <hr className={styles.connector} />}
                {showAsActive && (
                    <>
                        {withoutForm ? (
                            <div>{children}</div>
                        ) : (
                            <form onSubmit={onSubmit}>
                                {children}

                                <Button type="submit" loading={isLoading || isSubmitting}>
                                    {buttonText || 'Weiter'}
                                </Button>
                                {buttonDisclaimer && <div className={styles.disclaimer}>
                                    {buttonDisclaimer}
                                </div>}
                            </form>
                        )}
                    </>
                )}
            </section>
        </div>
    );
};

Step.displayName = 'Step';
export default Step;
